import React, { useState, useEffect } from 'react';
import './GuideMe.css';

export default function GuideMe({ onClose, initialSection = 'general' }) {
  const [activeSection, setActiveSection] = useState(initialSection);

  return (
    <div className="guide-overlay" onClick={onClose}>
      <div className="guide-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="guide-header">
          <h2>Guide Me</h2>
          <button className="guide-close-btn" onClick={onClose}>×</button>
        </div>

        {/* Tab Navigation */}
        <div className="guide-tabs">
          <button
            className={`guide-tab ${activeSection === 'general' ? 'active' : ''}`}
            onClick={() => setActiveSection('general')}
          >
            Investment Insights
          </button>
          <button
            className={`guide-tab ${activeSection === 'features' ? 'active' : ''}`}
            onClick={() => setActiveSection('features')}
          >
            Features Guide
          </button>
        </div>

        {/* Content Area */}
        <div className="guide-content">
          {activeSection === 'general' ? <GeneralContent /> : <FeaturesContent />}
        </div>
      </div>
    </div>
  );
}

// General - AlgoreitAI Benefits & Market Selection Impact
function GeneralContent() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/guide_me/summary.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSummary(data);
      } catch {
        // Silent failure – fall back to static copy
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const avgTop = summary?.avgTopPriceYoYPct ?? null;
  const avgBottom = summary?.avgBottomPriceYoYPct ?? null;
  const spread = summary?.spreadPriceYoYPct ?? null;

  const topExamples = (summary?.top10 || []).slice(0, 3);
  const bottomExamples = (summary?.bottom10 || []).slice(0, 3);

  return (
    <div className="guide-section">
      <h3>📊 AlgoreitAI: The Power of Market Selection</h3>
      <p className="guide-intro">
        <strong>Selecting the appropriate market is a critical factor in investment performance.</strong> AlgoreitAI 
        provides data-driven insights to identify high-performing markets and avoid underperforming ones, potentially 
        enhancing your annual returns significantly.
      </p>

      <div className="guide-topic">
        <h4>📈 The Market Selection Advantage: 20-25% Annual Spread</h4>
        <p>
          <strong>The difference between selecting an optimal market versus a suboptimal one can represent 20-25% in annual returns.</strong>
        </p>
        <p>
          {summary && avgTop != null && avgBottom != null && spread != null ? (
            <>
              The current top 10 <strong>Best Performing</strong> cities have an average{' '}
              <strong>Price YoY of {avgTop.toFixed(1)}%</strong>, while the 10 worst have an average{' '}
              <strong>Price YoY of {avgBottom.toFixed(1)}%</strong>, creating a spread of{' '}
              <strong>{spread.toFixed(1)} percentage points</strong>.
            </>
          ) : (
            <>
              The current top 10 <strong>Best Performing</strong> cities show strong positive annual price growth,
              while the 10 worst markets are experiencing double-digit declines.
            </>
          )}
        </p>
        
        <div style={{ 
          margin: '20px 0', 
          padding: '20px', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '8px',
          color: 'white'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: 'white' }}>💰 Real Investment Impact</h4>
          <p style={{ margin: '8px 0', fontSize: '16px' }}>
            <strong>$100,000 invested in the BEST market:</strong> $111,000 after 1 year (+$11,000)
          </p>
          <p style={{ margin: '8px 0', fontSize: '16px' }}>
            <strong>$100,000 invested in the WORST market:</strong> $86,000 after 1 year (-$14,000)
          </p>
          <p style={{ margin: '16px 0 8px 0', fontSize: '18px', fontWeight: 'bold' }}>
            ⚡ Difference: $25,000 (25%) in one year
          </p>
        </div>

        <div style={{ 
          margin: '24px 0', 
          border: '2px solid #667eea', 
          borderRadius: '8px', 
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <img 
            src="/guide_me/top_10_best_vs_worst_cities.png" 
            alt="Top 10 Best vs Worst Performing Cities"
            style={{ width: '100%', display: 'block' }}
          />
          <div style={{ padding: '12px', background: '#f8f9fa', fontSize: '13px', color: '#666', textAlign: 'center' }}>
            Current market performance showing the 20-25% spread between best and worst markets
          </div>
        </div>
      </div>

      <div className="guide-topic">
        <h4>💡 Key Insights from Current Data</h4>
        {topExamples.length > 0 && (
          <>
        <p><strong>Top Performers (Green bars on chart):</strong></p>
        <ul>
              {topExamples.map((c) => (
                <li key={`top-${c.city}-${c.state}`}>
                  <strong>{c.city}, {c.state}:</strong> {c.priceYoYPct.toFixed(1)}% annual Price YoY
                </li>
              ))}
        </ul>
          </>
        )}
        {bottomExamples.length > 0 && (
          <>
        <p><strong>Underperformers (Red bars on chart):</strong></p>
        <ul>
              {bottomExamples.map((c) => (
                <li key={`bottom-${c.city}-${c.state}`}>
                  <strong>{c.city}, {c.state}:</strong> {c.priceYoYPct.toFixed(1)}% annual Price YoY
                </li>
              ))}
        </ul>
          </>
        )}
      </div>

      <div className="guide-topic">
        <h4>📊 Three Strategic Advantages of AlgoreitAI</h4>
        
        <p><strong>1. Risk Mitigation</strong></p>
        <p>
          Early identification of declining markets enables proactive avoidance of underperformers that may experience 
          declines of 10-14% annually. This risk management approach provides substantial capital preservation and 
          protects investment portfolios from significant losses.
        </p>
      
        <p><strong>2. Opportunity Identification</strong></p>
        <p>
          Our turning point -TP detection algorithm identifies markets at critical transition phases when they shift from 
          decline to growth trends. These market inflection points represent optimal entry opportunities with 
          favorable risk-reward profiles for strategic investors.
        </p>
      
        <p><strong>3. Entry Timing Optimization</strong></p>
        <p>
          Combining Best Potential Scores with turning point data enables systematic identification of both optimal 
          markets and precise entry timing. Historical analysis demonstrates that markets with recent turning points 
          (0-12 months) typically present the highest upside potential and strongest momentum characteristics.
        </p>
      </div>
    
      <div className="guide-topic">
        <h4>📊 How AlgoreitAI Provides Competitive Advantages</h4>
        <p>
          <strong>AlgoreitAI analyzes over 800 major cities, thousands of zipcodes, and neighborhoods across US metropolitan areas</strong> to 
          identify optimal investment opportunities and help investors avoid underperforming markets. Our platform provides:
        </p>
        <ul>
          <li><strong>Best Potential Scoring:</strong> Rank markets by investment opportunity based on multiple indicators</li>
          <li><strong>Turning Point (TP) Detection:</strong> Identify markets at critical transition points for optimal entry timing</li>
          <li><strong>Performance Rankings:</strong> Comprehensive view of market performance trends</li>
          <li><strong>Multi-Metric Analysis:</strong> Track Price, Rent, Inventory, and Days on Market across all geographic levels</li>
        </ul>
      </div>
    
      <div className="guide-topic">
        <h4>📊 Real-World Success Scenarios</h4>
        
        <div style={{ 
          background: '#f0f7ff', 
          padding: '16px', 
          borderRadius: '8px',
          borderLeft: '4px solid #667eea',
          marginBottom: '16px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#667eea' }}>✅ Scenario 1: Utilizing Best Potential Scoring</p>
          <p style={{ margin: '0', fontSize: '14px' }}>
            Investor leverages AlgoreitAI to identify Paducah, KY (Best Potential Score &gt;75) with recent turning point.
            Result: <strong>+11% annual return</strong> versus market average of 3-5%.
            <br/><strong>Additional performance: 6-8% annually, representing $6,000-$8,000 per $100K invested</strong>
          </p>
        </div>
      
        <div style={{ 
          background: '#fff0f0', 
          padding: '16px', 
          borderRadius: '8px',
          borderLeft: '4px solid #e74c3c',
          marginBottom: '16px'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#e74c3c' }}>❌ Scenario 2: Insufficient Market Analysis</p>
          <p style={{ margin: '0', fontSize: '14px' }}>
            Investor proceeds with investment in Florida coastal market without comprehensive data analysis.
            Result: <strong>-12% annual decline</strong> (among the worst performers).
            <br/><strong>Potential loss mitigation with AlgoreitAI: $12,000+ per $100K invested</strong>
          </p>
        </div>
      
        <div style={{ 
          background: '#f0fff4', 
          padding: '16px', 
          borderRadius: '8px',
          borderLeft: '4px solid #2ecc71'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#2ecc71' }}>📊 Combined Impact</p>
          <p style={{ margin: '0', fontSize: '14px' }}>
            By selecting an optimal market (+11%) versus a suboptimal market (-12%), the investor achieves 
            <strong> 23 percentage points</strong> of performance differential.
            <br/><strong>On a $100K investment: $23,000 differential in the first year</strong>
          </p>
        </div>
      </div>

      <div className="guide-topic">
        <h4>📋 Effective Utilization of AlgoreitAI</h4>
        <p><strong>Step 1:</strong> Use the <strong>New Markets TP</strong> feature to identify markets with Best Potential Score &gt;70</p>
        <p><strong>Step 2:</strong> Filter for recent turning points (0-18 months) to optimize entry timing</p>
        <p><strong>Step 3:</strong> Cross-reference with the <strong>Best Performing</strong> feature to validate momentum trends</p>
        <p><strong>Step 4:</strong> Review comprehensive snapshots and historical charts to assess market fundamentals</p>
        <p><strong>Step 5:</strong> Generate Executive Reports for your top 3-5 investment candidates</p>
        <p><strong>Step 6:</strong> Make data-driven investment decisions based on comprehensive market analysis</p>
      </div>

      <div className="guide-action">
        <p style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '12px' }}>
          Getting Started
        </p>
        <p style={{ marginBottom: '8px' }}>
          <strong>Key Insight:</strong> Market selection is a primary determinant of real estate investment performance, 
          with potential impact differentials of 20-25 percentage points annually between optimal and suboptimal markets.
        </p>
        <p style={{ margin: '0' }}>
          💡 <strong>Next Steps:</strong> Select the "Features Guide" tab above to learn platform functionality, 
          or close this guide and click <strong>"New Markets TP"</strong> to begin identifying high-potential investment opportunities.
        </p>
      </div>
    </div>
  );
}

// Features Guide - Dashboard Usage Content
function FeaturesContent() {
  return (
    <div className="guide-section">
      <h3>📚 Dashboard Features Guide</h3>
      <p className="guide-intro">
        Comprehensive guide to platform features for data-driven investment analysis. The features below are described in the same order they appear on the dashboard row - From left to right.
      </p>
      <p className="guide-intro">
        There are three main ways to begin: (1) use <strong>Data Snapshot</strong> to get a quick view of your investment location and explore its charts, 
        (2) start with <strong>Best Performing</strong> to find the top-ranked markets at your chosen level, and (3) use <strong>New Markets TP</strong> 
        to focus on markets near recent turning points with high potential.
      </p>

      <div className="guide-topic">
        <h4>📍 Feature - Data Snapshot (Direct Selection)</h4>
        <p><strong>Purpose:</strong> Quickly access detailed data for any specific area.</p>
        <p><strong>How to Use:</strong></p>
        <ol>
          <li>Select geographic level (Metro (Metropolis), State, County, City, Zipcode, Neighborhood)</li>
          <li>Search for area name in the dropdown</li>
          <li>View snapshot with current values, forecasts, and recent changes</li>
          <li>Click "Charts" to see historical trends</li>
        </ol>
        <p><strong>When to Use:</strong> You know exactly which area you want to analyze</p>
      </div>

      <div className="guide-topic">
        <h4>🥇 Feature - Best Performing</h4>
        <p><strong>Purpose:</strong> Identify and rank markets by performance metrics.</p>
        <p>
          <strong>How the score works:</strong> Best Performing ranks markets using five recent performance signals: 12‑month and 3‑month price change,
          12‑month rent change, days on market, and inventory trend. It favors markets where prices and rents are rising, days on market are low,
          and inventory is tightening, so Rank 1 represents the strongest overall recent performance.
        </p>
        <p><strong>How to Use:</strong></p>
        <ol>
          <li>Choose geographic level</li>
          <li>Click "Best Performing" button</li>
          <li>Review rankings table (Rank 1 = best performer)</li>
          <li>Click "Data" button on any row for detailed snapshot</li>
        </ol>
        <p><strong>When to Use:</strong> Starting market research or monitoring portfolio position</p>
      </div>

      <div className="guide-topic">
        <h4>🔔 Feature - New Markets TP (Turning Point)</h4>
        <p>
          <strong>Purpose:</strong> Identify markets at critical turning points with high investment potential, with the strongest 
          potential typically occurring within 0–3 years of the turning point.
        </p>
        <p><strong>How to Use:</strong></p>
        <ol>
          <li>Select geographic level</li>
          <li>Click "New Markets TP" button</li>
          <li>Review Best Potential Scores (higher = better opportunity)</li>
          <li>Check turning point dates and status</li>
          <li>Charts load automatically with turning point markers</li>
        </ol>
        <p><strong>Best Potential Score Guide:</strong></p>
        <ul>
          <li><strong>&gt;80:</strong> Exceptional opportunity, multiple strong signals</li>
          <li><strong>70-80:</strong> Strong opportunity, favorable conditions</li>
          <li><strong>60-70:</strong> Moderate opportunity, selective investment</li>
          <li><strong>50-60:</strong> Limited opportunity, proceed with caution</li>
        </ul>
        <p><strong>When to Use:</strong> Timing market entry/exit or finding highest-potential investments</p>
      </div>

      <div className="guide-topic">
        <h4>📋 Feature - Executive Report</h4>
        <p><strong>Purpose:</strong> Comprehensive market summary for quick review or presentations.</p>
        <p><strong>How to Use:</strong></p>
        <ol>
          <li>Select a market from any feature</li>
          <li>Click "Executives Report" button</li>
          <li>Review snapshot, recent changes, and four charts (Price, Days, Inventory, Rent)</li>
          <li>Click "Print PDF" for clean export</li>
        </ol>
        <p><strong>When to Use:</strong> Preparing investment proposals, portfolio reviews, or stakeholder reporting</p>
      </div>

      <div className="guide-topic">
        <h4>🧭 Navigation & Tools</h4>
        <p><strong>Level Selector:</strong> Changes data granularity (top of dashboard)</p>
        <p><strong>Breadcrumbs:</strong> Shows geographic hierarchy, click to navigate up/down</p>
        <p><strong>Charts Button:</strong> Opens historical trend visualization</p>
        <p><strong>Data Button:</strong> In ranking tables, opens detailed snapshot for selected area</p>
        <p><strong>Search/Dropdown:</strong> Type-ahead area selection with autocomplete</p>
      </div>

      <div className="guide-topic">
        <h4>📊 Comprehensive Market Analysis Workflow</h4>
        <p>
          <strong>Three ways to approach your analysis:</strong> If you already have an investment location, start with 
          <strong> Data Snapshot</strong> and its charts to understand that specific area. If you are looking for a new investment in a strong, 
          established market, begin with <strong>Best Performing</strong>. If you want to catch markets just as a new rally is starting, use 
          <strong> New Markets TP</strong> to focus on recent turning points with high potential.
        </p>
        <ol>
          <li>Begin with Best Performing to identify top-ranked markets at your chosen geographic level</li>
          <li>For promising markets, click the "Data" button to open a snapshot and drill into the selected area</li>
          <li>Use New Markets TP to check turning point timing and Best Potential Scores (focus on Score &gt;70) for your short list</li>
          <li>Review comprehensive snapshots and historical chart patterns for each market</li>
          <li>Generate Executive Reports for finalist candidates to consolidate analysis</li>
          <li>Make informed investment decisions based on comprehensive multi-factor evaluation</li>
        </ol>
      </div>

      <div className="guide-action">
        <p>
          💡 <strong>Professional Tip:</strong> Utilize breadcrumb navigation to drill down from Metro → County → City → Neighborhood
          levels for granular market analysis.
        </p>
      </div>
    </div>
  );
}

